import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { GET_MOBILE_APP_INFO, GET_USER_STATS } from 'apollo/queries';
import { useGetParams } from 'components/evieOnboarding/utils/useGetParams';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { usePremiumEntitlement } from 'utils/hooks/usePremiumEntitlement';
import { identifyAnalyticsFromToken } from 'utils/analytics';
import { ROUTES } from 'utils/constants';
import { KEYS, USER_ROLES } from 'components/evieOnboarding/utils/constants';
import { PROGRAM_STATUS, resolveLevelAndSession } from './utils';

const EvieAppContext = createContext();

const EvieAppProvider = ({ children }) => {
  const now = moment();
  const {
    i18n: { language },
  } = useTranslation();
  const languageWithoutRegionCode = language.slice(0, 2);
  const [userToken, setUserToken] = useLocalStorage(KEYS.USER_TOKEN);
  const { urlToken, push } = useGetParams();
  const token = urlToken || userToken;

  const {
    loading,
    data: {
      getIndividualInfo: {
        id = '',
        name = '',
        email = '',
        oldProgramPayment = false,
        calAppointments = [],
        currentProgram: { planKey = '' } = {},
        patientPrograms = [],
      } = {},
    } = {},
    refetch,
  } = useQuery(GET_MOBILE_APP_INFO, {
    skip: !urlToken && !userToken, // Skip the query if token is not present
    context: {
      headers: {
        language: languageWithoutRegionCode,
        authorization: token ? `Bearer ${token}` : '',
        role: USER_ROLES.INDIVIDUAL,
      },
    },
  });
  const { data: { getPatientStats = {} } = {}, refetch: refetchStats } = useQuery(GET_USER_STATS, {
    skip: !urlToken && !userToken,
    context: {
      headers: {
        language: language.slice(0, 2),
        authorization: token ? `Bearer ${token}` : '',
        role: 'individual',
      },
    },
  });
  const { isPremium, isPending: isEntitlementPending } = usePremiumEntitlement({ appUserId: id, oldProgramPayment });

  const activeProgram = patientPrograms.find((patientProgram) => patientProgram.status === PROGRAM_STATUS.ACTIVE) || {};
  const {
    program: { programLevels = [], title = '' } = {},
    currentLevelCompletedActivities = [],
    currentSessionCompletedActivities = [],
    currentLevelNumber = 1,
    currentSessionNumber = 1,
    lastSessionCompletedAt,
  } = activeProgram;

  // Free users stay pinned to level 1 (today's behaviour); only premium follows currentLevelNumber.
  const targetLevelNumber = isPremium ? currentLevelNumber : 1;
  // One session per calendar day: a session completed today stays on screen (fully checked)
  // until tomorrow instead of immediately advancing to the next session.
  const isSessionCompletedToday = Boolean(lastSessionCompletedAt) && moment(lastSessionCompletedAt).isSame(now, 'day');

  const { levelData, sessionData } = useMemo(
    () =>
      resolveLevelAndSession({
        programLevels,
        levelNumber: targetLevelNumber,
        sessionNumber: currentSessionNumber,
        isSessionCompletedToday,
      }),
    [programLevels, targetLevelNumber, currentSessionNumber, isSessionCompletedToday],
  );
  const { programSessions = [], readings = [], audios = [], shortVideos = [] } = levelData;
  const { programSessionExercises: circuit = [], programSessionSqueezes: squeezes = [] } = sessionData;

  const additionalContent = useMemo(
    () => [...readings, ...audios, ...shortVideos].sort(() => Math.random() - 0.5),
    [readings, audios, shortVideos],
  );
  const [{ activities: completedActivities = [] } = {}] = currentLevelCompletedActivities;
  const [{ activities: completedExSqActivities = [] } = {}] = currentSessionCompletedActivities;
  const personalInfo = {
    id,
    name,
    email,
    planKey,
    currentProgram: title,
    currentWeek: levelData.number || targetLevelNumber,
    currentDay: sessionData.number || currentSessionNumber,
    totalDays: programSessions.length,
    completedDate: lastSessionCompletedAt,
    dayContent: [...circuit, ...squeezes],
  };
  const isAppointmentScheduled = calAppointments && calAppointments.length > 0;
  const appointmentDate = isAppointmentScheduled ? moment(calAppointments[0].scheduledAt) : null;
  const isAppointmentActive = (appointmentDate && appointmentDate.isAfter(now)) || !isAppointmentScheduled;

  const [identifiedToken, setIdentifiedToken] = useState(null);
  const analyticsReady = Boolean(token) && identifiedToken === token;

  useEffect(() => {
    // handle error here? if error then push to onboarding?
    if (!userToken && !urlToken) {
      push(ROUTES.EVIE_LOGIN);
    }
    if (urlToken) {
      setUserToken(urlToken);
    }
  }, [userToken, urlToken]);

  useLayoutEffect(() => {
    if (!token) return;
    identifyAnalyticsFromToken(token);
    setIdentifiedToken(token);
  }, [token]);

  const refetchContent = () => {
    refetch();
    refetchStats();
  };

  return (
    <EvieAppContext.Provider
      value={{
        // The tier is part of what's loading: reporting ready early paints the free
        // experience and then swaps it for the paid one.
        loading: loading || isEntitlementPending,
        isPremium,
        personalInfo,
        program: activeProgram.program,
        additionalContent,
        userStats: getPatientStats,
        activeProgram,
        refetch: refetchContent,
        completedActivities,
        completedExSqActivities,
        isAppointmentScheduled,
        isAppointmentActive,
        appointmentDate,
      }}
    >
      {analyticsReady ? children : null}
    </EvieAppContext.Provider>
  );
};

EvieAppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useEvieAppContext() {
  const context = useContext(EvieAppContext);
  if (!context) {
    throw new Error('Expected an EvieAppContext somewhere in the react tree to set context value');
  }
  return context;
}

// For components rendered both inside and outside the provider, such as BottomNav on /download.
function useOptionalEvieAppContext() {
  return useContext(EvieAppContext) || {};
}

export { useEvieAppContext, useOptionalEvieAppContext, EvieAppProvider };
